import { createLink } from "@tanstack/react-router";
import { Button as RACButton } from "react-aria-components/Button";
import { composeRenderProps } from "react-aria-components/composeRenderProps";
import { Link as RacLink } from "react-aria-components/Link";
import { cn, tv } from "tailwind-variants";
import type {
	ButtonLinkProps,
	ButtonProps,
} from "#/_packages/umbrella/features/button/types";
import UmbBtnSpriteIcon from "#/_packages/umbrella/features/icons/components/umb.btn-sprite-icon.tsx";

function getLinkRel(
	rel: string | undefined,
	noFollow: boolean | undefined,
	target: string | undefined,
) {
	const values = new Set(rel?.split(/\s+/).filter(Boolean));
	if (noFollow) values.add("nofollow");
	if (target === "_blank") {
		values.add("noopener");
		values.add("noreferrer");
	}
	return values.size > 0 ? [...values].join(" ") : undefined;
}
export const button = tv({
	base: "btn",
	variants: {
		variant: {
			solid: "",
			outline: "btn-outline",
			ghost: "btn-ghost",
			soft: "btn-soft",
			link: "btn-link",
			none: "btn-none",
			quiet: "btn-quiet",
		},
		intent: {
			primary: "btn-primary",
			secondary: "btn-secondary",
			light: "btn-light",
			dark: "btn-dark",
			white: "btn-white",
			black: "btn-black",
			success: "btn-success",
			warning: "btn-warning",
			danger: "btn-danger",
			info: "btn-info",
			none: "btn-none",
		},
		size: {
			extraSmall: "btn-xs",
			small: "btn-sm",
			medium: "",
			large: "btn-lg",
			extraLarge: "btn-xl",
			fullWidth: "btn-full-width",
		},
		displayBlock: {
			true: "btn-full-width",
		},
		hideLabel: {
			true: "btn-icon-only",
		},
		alignmentSelfVertical: {
			stretch: "",
			start: "self-start",
			center: "self-center",
			end: "self-end",
		},
		rounded: {
			default: "",
			none: "rounded-0",
			xs: "rounded-(--umb-border-radius-xs)!",
			sm: "rounded-(--umb-border-radius-sm)!",
			md: "rounded-(--umb-border-radius-md)!",
			lg: "rounded-(--umb-border-radius-lg)!",
			xl: "rounded-(--umb-border-radius-xl)!",
			full: "rounded-(--umb-border-radius-pill)!",
		},
	},
	defaultVariants: {
		variant: "solid",
		intent: "primary",
		size: "medium",
	},
});

function Button(props: ButtonProps) {
	const {
		variant,
		intent,
		size,
		displayBlock,
		rounded,
		hideLabel,
		alignmentSelfVertical,
		...buttonProps
	} = props;

	return (
		<RACButton
			{...buttonProps}
			className={composeRenderProps(props.className, (className, renderProps) =>
				button({
					...renderProps,
					variant,
					intent,
					size,
					displayBlock,
					hideLabel,
					alignmentSelfVertical,
					rounded,
					className,
				}),
			)}
		>
			{composeRenderProps(props.children, (children) => (
				<>{children}</>
			))}
		</RACButton>
	);
}

const LinkButton = (props: ButtonLinkProps) => {
	const {
		variant,
		intent,
		size,
		displayBlock,
		hideLabel,
		alignmentSelfVertical,
		noFollow,
		className,
		...linkProps
	} = props;
	const rel = getLinkRel(linkProps.rel, noFollow, linkProps.target);

	const styles = button({
		variant,
		intent,
		size,
		displayBlock,
		hideLabel,
		alignmentSelfVertical,
	});

	return (
		<RacLink {...linkProps} rel={rel} className={cn(styles, className)}>
			{composeRenderProps(props.children, (children) => (
				<>{children}</>
			))}
		</RacLink>
	);
};

const UmbLinkButton = Object.assign(createLink(LinkButton), {
	Content: ButtonContent,
});

function ButtonContent({
	spriteIcon,
	label,
	hideLabel,
	iconPosition = "start",
}: {
	spriteIcon?: string | null;
	iconPosition?: string | null;
	label: string;
	hideLabel?: boolean;
}) {
	const icon = spriteIcon ? <UmbBtnSpriteIcon icon={spriteIcon} /> : null;
	const text = hideLabel ? (
		<span className="sr-only">{label}</span>
	) : (
		<span className="text-label">{label}</span>
	);

	return (
		<>
			{iconPosition === "end" ? null : icon}
			{text}
			{iconPosition === "end" ? icon : null}
		</>
	);
}

LinkButton.Content = ButtonContent;
Button.Content = ButtonContent;

export { Button, LinkButton, UmbLinkButton };
